import "./TeamMemberBandwidth.scss";

import Card from "components/Card/Card";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetBandwidthQuery } from "services/team-bandwidth-api";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import { useToken } from "utils/context";

const BandwidthItem = React.memo(
  ({
    title,
    value,
    width,
  }: {
    title: string;
    value: number;
    width: number;
  }) => (
    <div className="grid-layout ai-c">
      <span className="year">{title}</span>
      <div
        className="bar"
        style={{
          width: `${width}%`,
        }}
        data-percentage={value}
      ></div>
    </div>
  ),
);

const TeamMemberBandwidth = () => {
  const { accessToken } = useToken();
  const { fileId } = useParams<{ fileId: string }>();
  const { data: bandwidth, isLoading } = useGetBandwidthQuery(
    { fileId },
    {
      skip: !accessToken || !fileId,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: orgSettings } = useGetOrganizationSettingsQuery(undefined, {
    skip: false,
  });

  const maxValue = useMemo(() => {
    if (!bandwidth) return 0;

    const memberValues = bandwidth.team_members?.map((m) => m.task_count) || [];
    const teamValues = bandwidth.teams?.map((t) => t.task_average) || [];
    return Math.max(...[...memberValues, ...teamValues], 1);
  }, [bandwidth]);

  const renderBandwidthItems = useMemo(() => {
    if (!bandwidth) return null;

    return (
      <>
        {bandwidth.team_members?.map((m, index) => (
          <BandwidthItem
            key={`member-${index}`}
            title={m.full_name}
            width={(100 * m.task_count) / maxValue}
            value={m.task_count}
          />
        ))}
        {bandwidth.teams?.map((team, index) => (
          <BandwidthItem
            key={`team-${index}`}
            title={`${team.name} Avg`}
            value={team.task_average}
            width={(100 * team.task_average) / maxValue}
          />
        ))}
      </>
    );
  }, [bandwidth, maxValue]);

  if (isLoading) {
    return (
      <Card heading={`${orgSettings?.name || ""} Team Member Bandwidth`}>
        <div className="loading-state">Loading bandwidth data...</div>
      </Card>
    );
  }

  return (
    <Card heading={`${orgSettings?.name || ""} Team Member Bandwidth`}>
      <section className="bar-graph bar-graph-horizontal">
        {renderBandwidthItems}
      </section>

      <div className="tags corner-number w-txt">
        <div style={{ minWidth: "300px" }}>
          * Numbers = Files Currently Assigned *
        </div>
      </div>
    </Card>
  );
};

export default React.memo(TeamMemberBandwidth);
